import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getKingdomDiplomacy } from '../api/diplomacy';
import type { KingdomDiplomacyResponse } from '../types/diplomacy';

interface CacheEntry {
  data: KingdomDiplomacyResponse;
  expiresAt: number;
}
const cache = new Map<string, CacheEntry>();
const CACHE_TTL_MS = 60_000;

export function _resetDiplomacyCache() {
  cache.clear();
}

interface UseDiplomacyResult {
  data: KingdomDiplomacyResponse | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<boolean>;
}

export function useDiplomacy(kingdomId: string | null): UseDiplomacyResult {
  const { worldId } = useAuth();
  const [data, setData] = useState<KingdomDiplomacyResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDiplomacy = useCallback(async (bust = false): Promise<boolean> => {
    if (!kingdomId || !worldId) {
      setData(null);
      return false;
    }

    const cacheKey = `${worldId}:${kingdomId}`;
    const now = Date.now();
    const cached = cache.get(cacheKey);

    if (!bust && cached && cached.expiresAt > now) {
      setData(cached.data);
      return true;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await getKingdomDiplomacy(worldId, kingdomId);
      cache.set(cacheKey, { data: result, expiresAt: now + CACHE_TTL_MS });
      setData(result);
      return true;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load diplomacy data');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [kingdomId, worldId]);

  // Always busts the cache, so refresh() always reflects the freshly-fetched result's own
  // success/failure — never a stale cache hit's implicit success (SF-158).
  const refresh = useCallback(async (): Promise<boolean> => {
    if (kingdomId && worldId) {
      cache.delete(`${worldId}:${kingdomId}`);
    }
    return fetchDiplomacy(true);
  }, [fetchDiplomacy, kingdomId, worldId]);

  useEffect(() => {
    fetchDiplomacy();
  }, [fetchDiplomacy]);

  return { data, isLoading, error, refresh };
}
